/* ─────────────────────────────────────────────────────────────────────────
   What each plan is allowed to do — the single source of truth.

   This file is **owned here and copied verbatim into website-cloud,
   website-enterprise, review, scope and website-lms**. All six Workers enforce
   limits and they have to agree; before this existed the vocabulary lived in
   two places (`PlanKey` here, `PLAN_STORAGE_MB`/`PLAN_REVISIONS` in Cloud's
   `worker/env.ts`) with nothing keeping them in step.

   Six copies is four more than is comfortable. Change it here first, then
   propagate with `node scripts/sync-shared.mjs` from the workspace repo —
   never a hand `cp`. `shared/copies.test.ts` in each of the six fails when a
   copy is edited in place.

   It said "website-cloud, review and scope" and "four Workers" until
   2026-08-29, having missed website-enterprise since that repo was written.
   It gained a sixth copy, in website-lms, on 2026-09-02.

   ── Where each limit is enforced ─────────────────────────────────────────
   · storageGb        → written to `cloud_users.storage_limit_mb`; charged by
                        `reserveStorage()` in Cloud's worker/lib/quota.ts
   · versionsKept     → `pruneRevisions()` in Cloud's worker/routes/files.ts
   · workspaces       → `requireWorkspaceQuota()` in Cloud's worker/lib/plan.ts
   · publishedCourses → `requirePublishQuota()` in the same file
   · reviewProjects   → `requireProjectQuota()` in Review's worker/lib/plan.ts
   · scopeProjects    → `requireProjectQuota()` in Scope's worker/lib/plan.ts
   · composeExport    → the desktop app, from GET /api/auth/me
   · cloudAppSync     → `start` in Cloud's worker/routes/files.ts, on the
                        node's source app
   · maxFileGb        → `start` in the same handler
   · fileEmbeds       → `create` in Cloud's worker/routes/embeds.ts, and the
                        Files page's share dialog
   · auditLog, sso, customDomain, chatChannels
                      → Cloud's Enterprise surfaces, each double-gated (server
                        for truth, client so the feature reads as paid)
   · lms              → `requireLms(c, {teacher: true})` in the LMS Worker
                        (a teacher write, never a learner's own progress); and
                        `canUploadToLms()` in Compose
   · assist           → false in every tier. An organisation switches Assist on
                        as an extra (`organisations.assist_enabled` in
                        website-enterprise); no plan includes it

   ── Why `free` and not `demo` ────────────────────────────────────────────
   The tier is *displayed* as "Demo", because losing export is the whole point
   of it and "Free" undersells that. The key stays `free`: it is the value
   already in `cloud_users.plan`, in the account rows and in every audit entry,
   and renaming it would be a data migration for a label. `TIER_LABEL` is the
   one place the two are connected.

   ── Workspaces no longer have their own pool ─────────────────────────────
   They used to: a Pro workspace carried its own 200 GB and a Max one its own
   1 TB, on top of the owner's personal allowance. That made Max at £150 an
   effective 6 TB of storage, which is not a product — it is a hosting bill with
   a login. A workspace now draws on **its owner's** pool, so what a plan sells
   is one number instead of a multiplier. Enterprise keeps a large pool and
   spends it across as many workspaces as it likes.

   The consequence to keep in mind: `QuotaOwner` still has a `workspace` arm,
   but it resolves to the owner's `cloud_users` row. See `resolveQuotaOwner()`
   in Cloud's worker/lib/quota.ts — one place, so this cannot come back through
   a path someone forgot.
   ───────────────────────────────────────────────────────────────────────── */

/**
 * `developer` is Mauvely staff: `users.is_admin`, the flag the internal
 * console grants, which each reader of a plan checks beside `subscriptions`
 * (website-main `tierAndOrg`, Assist, Review and Scope `effectiveTier`). It is
 * a person's, never an organisation's (`isOrgPlan`), and it ranks above every
 * plan a person can buy.
 */
export type Tier = 'free' | 'pro' | 'max' | 'enterprise' | 'developer';

/** A count with no ceiling. `Infinity` would be correct and does not survive
 *  `JSON.stringify` — it becomes `null`, and `/api/auth/me` is consumed by two
 *  C++ clients that would read that as zero. */
export const UNLIMITED = -1;
export const isUnlimited = (n: number): boolean => n === UNLIMITED;
/** True when `used` is already at or past `limit`. Written once because every
 *  call site has to get the unlimited case right. */
export const atLimit = (used: number, limit: number): boolean =>
  !isUnlimited(limit) && used >= limit;

export interface Entitlements {
  /** Personal vault allowance. Workspaces this account owns are charged against
   *  this same pool — they have no separate allowance. */
  storageGb: number;
  /** How many revisions of a file survive `pruneRevisions`, including the live
   *  one. 1 means "no history". */
  versionsKept: number;
  /** Workspaces this account may **own**, or `UNLIMITED`. Being a member of
   *  someone else's is never gated: a workspace outlives any one subscription,
   *  and locking a lapsed member out of a shared drive punishes the wrong
   *  person. */
  workspaces: number;
  /** Courses that may be **live** at once, or `UNLIMITED`. Unpublishing frees a
   *  slot; the cap is on what is serving, not on what has ever been published. */
  publishedCourses: number;
  /**
   * Review projects that may be **open for comment** at once, or `UNLIMITED`.
   * 0 means the account has no Review at all.
   *
   * Archiving frees a slot, the same shape as `publishedCourses` — the cap is on
   * what reviewers can still reach, not on what has ever been sent. The numbers
   * are the ones mauvely.com already sells in `shared/pricing.ts`: the
   * comparison matrix has advertised "Active review links · Pro 3 ·
   * Max/Enterprise Unlimited" since before the product existed, so this table
   * follows that rather than the other way round.
   */
  reviewProjects: number;
  /**
   * Scope projects that may be **open** at once, or `UNLIMITED`. 0 means the
   * account has no Scope at all.
   *
   * Archiving frees a slot, the same shape as `reviewProjects`. Unlike Review
   * there is no marketing matrix to defer to — Scope predates its pricing page —
   * so the numbers are set here: Pro and up, uncapped. The door is the upsell,
   * not a cap.
   */
  scopeProjects: number;
  /** Compose may write a course package. Demo keeps the entire editor and the
   *  learner preview and loses only the export. */
  composeExport: boolean;
  /** Snap and Compose may upload to Cloud. Snap is otherwise fully functional
   *  and free — capture, library and annotation never touch this. */
  cloudAppSync: boolean;
  /** Largest single file, in GB. */
  maxFileGb: number;
  /** Per-link view and download counts over time. The counts are recorded for
   *  everyone; this gates reading them back. */
  linkAnalytics: boolean;

  /**
   * Embed a file: a public address for one file, meant to be pasted into
   * Discord or an `<img>` on somebody's own site.
   *
   * On the Max line with `linkAnalytics` and `cloudAppSync`, and true for
   * Enterprise for the same reason those are — an Enterprise contract is above
   * Max, not beside it.
   *
   * It is the one entitlement that turns E2EE **off** for a chosen file: a
   * separate copy leaves the vault and is served to anyone with the address.
   * Cloud's docs/SECURITY.md says so in its own section, and the dialog says it
   * before anybody clicks.
   */
  fileEmbeds: boolean;
  /** A reader over `cloud_audit`. */
  auditLog: boolean;
  /** Named channels in workspace chat, beyond the workspace-wide one and the
   *  per-project ones. */
  chatChannels: boolean;
  /** An organisation: seats, a shared pool, an admin dashboard. */
  organisation: boolean;
  /** `cloud.example.com` via CNAME, on top of the per-org subdomain. */
  customDomain: boolean;
  /**
   * The organisation replaces Mauvely's branding with its own — its name in
   * product names, its colour, its logo.
   *
   * Enterprise only, and not because it is expensive to serve: it is the shape
   * of a signed contract. An individual account has no organisation to brand,
   * and a self-serve tier that could rebrand the product would let anybody
   * publish a page that looks like somebody else's company.
   */
  branding: boolean;
  /** Single sign-on (OIDC: Entra ID, Google Workspace). Enterprise only;
   *  website-enterprise/docs/sso.md is the design. */
  sso: boolean;
  /**
   * The organisation's LMS, Learn. Enterprise only.
   *
   * Gates teacher writes in the LMS — creating, editing and publishing a
   * course — and the upload action in Compose. It never gates a learner's own
   * progress: saving where you are in a course is not a billing question, and
   * a lapsed plan must not cost anyone their place mid-course. What withholds
   * Learn from a person entirely is the access matrix, not this flag.
   */
  lms: boolean;
  /**
   * Assist, the AI app. **False in every tier**: it is an extra an
   * organisation switches on and is billed for by use, not something a plan
   * buys. The switch is `organisations.assist_enabled`, and `app:assist` in
   * `access.ts` decides who in the organisation gets it.
   */
  assist: boolean;
}

export const TIER_LABEL: Record<Tier, string> = {
  free: 'Demo',
  pro: 'Pro',
  max: 'Max',
  enterprise: 'Enterprise',
  developer: 'Developer'
};

export const TIERS: Record<Tier, Entitlements> = {
  free: {
    storageGb: 5,
    versionsKept: 1,
    workspaces: 0,
    publishedCourses: 0,
    reviewProjects: 0,
    scopeProjects: 0,
    composeExport: false,
    cloudAppSync: false,
    maxFileGb: 0.25,
    linkAnalytics: false,
    fileEmbeds: false,
    auditLog: false,
    chatChannels: false,
    organisation: false,
    customDomain: false,
    branding: false,
    sso: false,
    lms: false,
    assist: false
  },
  pro: {
    storageGb: 200,
    versionsKept: 5,
    // Workspaces moved to Max. Pro is the complete single-author plan: the whole
    // of Compose, real storage and real history. What it does not buy is a team.
    workspaces: 0,
    publishedCourses: 0,
    // Review is the one paid surface Pro keeps a capped amount of rather than
    // none of: three open at a time is enough for a freelancer running a client
    // review, and the Max upsell is the cap rather than the door.
    reviewProjects: 3,
    scopeProjects: UNLIMITED,
    composeExport: true,
    cloudAppSync: true,
    maxFileGb: 5,
    linkAnalytics: false,
    fileEmbeds: false,
    auditLog: false,
    chatChannels: false,
    organisation: false,
    customDomain: false,
    branding: false,
    sso: false,
    lms: false,
    assist: false
  },
  max: {
    storageGb: 1024,
    versionsKept: 20,
    workspaces: 1,
    publishedCourses: 15,
    reviewProjects: UNLIMITED,
    scopeProjects: UNLIMITED,
    composeExport: true,
    cloudAppSync: true,
    maxFileGb: 20,
    linkAnalytics: true,
    fileEmbeds: true,
    auditLog: false,
    chatChannels: false,
    organisation: false,
    customDomain: false,
    branding: false,
    sso: false,
    lms: false,
    assist: false
  },
  enterprise: {
    // The starting pool. Unlike the other tiers this is a floor rather than a
    // fixed grant — it is administered per contract, so `storage_limit_mb` on an
    // Enterprise account is authoritative and this value only seeds it.
    storageGb: 2048,
    versionsKept: 20,
    workspaces: UNLIMITED,
    publishedCourses: UNLIMITED,
    reviewProjects: UNLIMITED,
    scopeProjects: UNLIMITED,
    composeExport: true,
    cloudAppSync: true,
    maxFileGb: 20,
    linkAnalytics: true,
    fileEmbeds: true,
    auditLog: true,
    chatChannels: true,
    organisation: true,
    customDomain: true,
    branding: true,
    sso: true,
    lms: true,
    assist: false
  },
  developer: {
    // A petabyte rather than UNLIMITED: the desktop apps multiply `storageGb`
    // by 1024 into an integer, and `storageMbFor` would turn -1 into -1024.
    // Cloud stores a developer as unmetered itself (`planLimitMb` in its
    // quota.ts).
    storageGb: 1024 * 1024,
    // Finite for the same reason: revision pruning slices by it.
    versionsKept: 100,
    workspaces: UNLIMITED,
    publishedCourses: UNLIMITED,
    reviewProjects: UNLIMITED,
    scopeProjects: UNLIMITED,
    composeExport: true,
    cloudAppSync: true,
    maxFileGb: 1024,
    linkAnalytics: true,
    fileEmbeds: true,
    auditLog: true,
    chatChannels: true,
    // An organisation's contract, not a person's.
    organisation: false,
    customDomain: true,
    branding: false,
    sso: false,
    lms: true,
    assist: false
  }
};

export const isTier = (v: unknown): v is Tier =>
  v === 'free' || v === 'pro' || v === 'max' || v === 'enterprise' || v === 'developer';

/** A plan an organisation can be on: every tier but `developer`, which is a
 *  person's. */
export const isOrgPlan = (v: unknown): v is Exclude<Tier, 'developer'> => isTier(v) && v !== 'developer';

/**
 * A plan name this deployment recognises, or `free`.
 *
 * Everything reads limits through here rather than indexing `TIERS` directly.
 * Cloud's old `planOf` normalised against one of its two records and not the
 * other, so a tier added to one and forgotten in the other silently read as
 * free — a single table makes that shape of bug impossible.
 */
export const tierOf = (plan: string | null | undefined): Tier =>
  isTier(plan) ? plan : 'free';

export const entitlements = (plan: string | null | undefined): Entitlements =>
  TIERS[tierOf(plan)];

// ── unit helpers ────────────────────────────────────────────────────────────
// Cloud stores allowances in MB and charges in bytes. Base 1024 throughout,
// matching `BYTES_PER_MB` in Cloud's worker/env.ts — mixing bases here would
// under-report every quota by ~7%.

export const GB_TO_MB = 1024;
export const storageMbFor = (plan: string | null | undefined): number =>
  Math.round(entitlements(plan).storageGb * GB_TO_MB);
export const maxFileBytesFor = (plan: string | null | undefined): number =>
  Math.round(entitlements(plan).maxFileGb * 1024 * 1024 * 1024);

/**
 * Which tier a set of live subscriptions adds up to.
 *
 * Highest wins rather than newest: someone holding both a Pro and a Max row
 * mid-change should get Max, and a downgrade that has not taken effect yet must
 * not strip the plan they are still paying for.
 */
export function bestTier(plans: readonly string[]): Tier {
  if (plans.some((p) => p === 'developer')) return 'developer';
  if (plans.some((p) => p === 'enterprise')) return 'enterprise';
  if (plans.some((p) => p === 'max')) return 'max';
  if (plans.some((p) => p === 'pro')) return 'pro';
  return 'free';
}
